import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const plans = [
  {
    name: "Seedling",
    price: "Free",
    suffix: "/ forever",
    blurb: "Try the dashboard on a simulated home.",
    features: ["Live demo dashboard", "5 AI sustainability chats / week", "Manual usage logging", "Carbon footprint tracker"],
    cta: "Get started",
    href: "/register",
    accent: false,
  },
  {
    name: "Grove",
    price: "AED 49",
    suffix: "/ month",
    blurb: "One sensor, one home. Real telemetry, real savings.",
    features: ["1 Noa sensor included", "Unlimited room mapping", "Unlimited AI coach access", "Auto-saving recommendations", "Monthly impact report"],
    cta: "Start 30-day trial",
    href: "/register",
    accent: true,
  },
  {
    name: "Canopy",
    price: "AED 299",
    suffix: "/ month",
    blurb: "For multi-floor homes and properties that share power and water lines.",
    features: ["Up to 4 sensors included", "Multi-property dashboard", "Priority support", "Audit export to CSV / API", "Carbon offset auto-purchase", "One year cloud data storage"],
    cta: "Talk to sales",
    href: "/#waitlist",
    accent: false,
  },
];

export default function Pricing() {
  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="max-w-2xl">
          <p className="label-eyebrow mb-4">Pricing</p>
          <h1 className="font-serif text-5xl md:text-6xl font-light text-forest tracking-tighter leading-[0.95]">
            Plant once.<br />Save indefinitely.
          </h1>
          <p className="mt-6 text-lg text-ink/65 leading-relaxed">
            Most members recoup their plan in the first two months. Cancel anytime — the sensor is yours to keep.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-3xl p-8 border ${
                p.accent ? "bg-forest text-bone border-forest" : "bg-white border-[#E2DFD8]"
              } flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl`}
              data-testid={`plan-${p.name.toLowerCase()}`}
            >
              {p.accent && (
                <span className="absolute -top-3 left-8 bg-terracotta text-bone text-xs font-mono px-3 py-1 rounded-full">
                  most chosen
                </span>
              )}
              <p className={`label-eyebrow ${p.accent ? "text-bone/60" : ""}`}>{p.name}</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-5xl font-light">{p.price}</span>
                <span className={`text-sm ${p.accent ? "text-bone/60" : "text-ink/55"}`}>{p.suffix}</span>
              </div>
              <p className={`mt-3 text-sm ${p.accent ? "text-bone/70" : "text-ink/60"}`}>{p.blurb}</p>
              <ul className="mt-8 space-y-3 text-sm flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={16} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${p.accent ? "text-terracotta" : "text-forest"}`} />
                    <span className={p.accent ? "text-bone/85" : "text-ink/75"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.href} className="mt-8 block" data-testid={`plan-cta-${p.name.toLowerCase()}`}>
                <Button
                  className={`w-full rounded-full h-12 ${
                    p.accent
                      ? "bg-bone text-forest hover:bg-bone/90"
                      : "bg-forest text-bone hover:bg-forest-hover"
                  }`}
                >
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-12 text-center text-sm text-ink/55">
          Every plan offsets the manufacturing footprint of its sensor within 90 days.
        </p>
        <p className="mt-3 text-center">
          <Link to="/home-billing" className="text-sm text-forest hover:text-forest-hover underline underline-offset-4 decoration-forest/30" data-testid="link-home-billing">
            See sample in-app billing dashboard →
          </Link>
        </p>

        {/* Property builders / developers */}
        <div className="mt-32 pt-16 border-t border-[#E2DFD8]" data-testid="builders-section">
          <div className="max-w-2xl">
            <p className="label-eyebrow mb-4">For property builders & developers</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tighter leading-[0.95]">
              Ship every unit<br />sustainability-ready.
            </h2>
            <p className="mt-6 text-lg text-ink/65 leading-relaxed">
              Deploy Noa across new-builds, renovations and multi-tower portfolios. Volume pricing per unit, centralized property dashboard, and white-label options at Enterprise.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="builder-plans">
            {[
              {
                name: "Starter",
                price: "From AED 29",
                suffix: "/ unit / month",
                blurb: "For boutique developers deploying 10-50 units.",
                features: ["Sensor per unit", "Tenant-facing Noa app", "Portfolio analytics dashboard", "Monthly ESG-ready report"],
              },
              {
                name: "Professional",
                price: "From AED 39",
                suffix: "/ unit / month",
                blurb: "For property managers with 50-500 units under management.",
                features: ["Everything in Starter", "Multi-property rollup views", "API + BMS integrations", "Priority technical support", "Quarterly on-site audit"],
                accent: true,
              },
              {
                name: "Enterprise",
                price: "Custom",
                suffix: "pricing",
                blurb: "For developers, REITs and municipalities at 500+ units.",
                features: ["Everything in Professional", "White-label mobile app", "Dedicated success engineer", "Custom SLAs & data residency", "Volume hardware discounts"],
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`relative rounded-3xl p-8 border transition-all hover:-translate-y-1 hover:shadow-xl ${
                  p.accent ? "bg-forest text-bone border-forest" : "bg-white border-[#E2DFD8]"
                } flex flex-col`}
                data-testid={`builder-plan-${p.name.toLowerCase()}`}
              >
                {p.accent && (
                  <span className="absolute -top-3 left-8 bg-terracotta text-bone text-xs font-mono px-3 py-1 rounded-full">
                    recommended
                  </span>
                )}
                <p className={`label-eyebrow ${p.accent ? "text-bone/60" : ""}`}>{p.name}</p>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className="font-serif text-4xl font-light">{p.price}</span>
                  <span className={`text-sm ${p.accent ? "text-bone/60" : "text-ink/55"}`}>{p.suffix}</span>
                </div>
                <p className={`mt-3 text-sm ${p.accent ? "text-bone/70" : "text-ink/60"}`}>{p.blurb}</p>
                <ul className="mt-8 space-y-3 text-sm flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${p.accent ? "text-terracotta" : "text-forest"}`} />
                      <span className={p.accent ? "text-bone/85" : "text-ink/75"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/#waitlist" className="mt-8 block" data-testid={`builder-cta-${p.name.toLowerCase()}`}>
                  <Button
                    className={`w-full rounded-full h-12 ${
                      p.accent
                        ? "bg-bone text-forest hover:bg-bone/90"
                        : "bg-forest text-bone hover:bg-forest-hover"
                    }`}
                  >
                    Talk to sales
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-ink/55">
            All builder plans include on-site installation training and a bulk sensor discount.
          </p>
          <p className="mt-3 text-center">
            <Link to="/property-billing" className="text-sm text-forest hover:text-forest-hover underline underline-offset-4 decoration-forest/30" data-testid="link-property-billing">
              See sample property billing dashboard →
            </Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
